import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { SEO, buildBreadcrumbSchema } from "@/components/seo/SEO";
import { FileText, CheckCircle, AlertCircle, Scale } from "lucide-react";

const Terms = () => {
  return (
    <div className="min-h-screen app-surface">
      <SEO
        title="Terms of Service — Email2Contact"
        description="Email2Contact Terms of Service for the Microsoft Outlook add-in. Provided by Celsios GmbH (Germany). License, acceptable use, billing, and liability."
        path="/terms"
        jsonLd={buildBreadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Terms", path: "/terms" },
        ])}
      />
      <Navbar />
      
      <main className="py-16 md:py-24">
        <div className="container px-4 max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 app-pill app-pill-primary mb-6">
              <Scale className="w-3.5 h-3.5" />
              <span>Legal Agreement</span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-3">
              Terms of Service
            </h1>
            <p className="text-muted-foreground">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          {/* Key Points */}
          <Card className="app-tile p-8 mb-12">
            <h2 className="font-display text-2xl font-semibold tracking-tight mb-6 flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-primary" />
              Agreement Overview
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Binding Agreement</h3>
                  <p className="text-sm text-muted-foreground">
                    By using Email2Contact, you agree to these terms
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Scale className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">German Law</h3>
                  <p className="text-sm text-muted-foreground">
                    These terms are governed by German law
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">License Grant</h3>
                  <p className="text-sm text-muted-foreground">
                    Limited, non-exclusive license to use the add-in
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <AlertCircle className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">No Warranty</h3>
                  <p className="text-sm text-muted-foreground">
                    Service provided "as is" without warranties
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Detailed Terms */}
          <div className="prose prose-slate max-w-none space-y-8">
            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4 flex items-center gap-2">
                <FileText className="w-6 h-6 text-primary" />
                1. Acceptance of Terms
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                These Terms of Service ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") 
                and Celsios GmbH ("we," "us," or "our"), a company registered in Germany, regarding your use of the 
                Email2Contact Outlook add-in ("Add-in," "Service").
              </p>
              <p className="text-muted-foreground leading-relaxed">
                By installing, accessing, or using Email2Contact, you acknowledge that you have read, understood, and agree 
                to be bound by these Terms. If you do not agree to these Terms, you must not use the Add-in.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">2. Service Provider</h2>
              <Card className="app-tile p-6 bg-muted/30">
                <p className="font-semibold mb-2">Celsios GmbH</p>
                <p className="text-sm text-muted-foreground">
                  Address: Cosimastrasse 121, 81925 Munich, Germany<br />
                  Amtsgericht Frankfurt am Main HRB 134969<br />
                  Managing Director: Justus Wiedemann<br />
                  Email: support@email2contact.com<br />
                  Website: https://email2contact.com
                </p>
              </Card>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">3. Description of Service</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Email2Contact is a productivity add-in for Microsoft Outlook that:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Extracts contact information from email signatures using AI-powered analysis (ConvertAPI EU and Azure OpenAI West Europe)</li>
                <li>Allows you to review and edit extracted contact data</li>
                <li>Saves contacts directly to your Microsoft Outlook/Microsoft 365 account</li>
                <li>Optionally classifies companies via Smart Categories (Azure OpenAI GPT-4o, West Europe)</li>
                <li>Optionally generates research notes via Smart Notes (Lovable AI / Google Gemini 2.5 Flash)</li>
                <li>All AI processing occurs in EU data centers with zero permanent data retention (ephemeral only)</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                The Service is designed to enhance productivity by automating the manual process of creating contacts 
                from email signatures while maintaining GDPR compliance and privacy-first AI processing.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">4. License and Usage Rights</h2>
              
              <h3 className="text-xl font-semibold mb-3">4.1 License Grant</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, 
                revocable license to use Email2Contact for your personal or internal business purposes.
              </p>

              <h3 className="text-xl font-semibold mb-3">4.2 Restrictions</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You agree not to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Modify, reverse engineer, decompile, or disassemble the Add-in</li>
                <li>Remove or alter any copyright, trademark, or proprietary notices</li>
                <li>Use the Add-in for any unlawful or fraudulent purpose</li>
                <li>Attempt to gain unauthorized access to our systems or networks</li>
                <li>Use the Add-in in any way that could damage, disable, or impair the Service</li>
                <li>Resell, sublicense, or otherwise commercialize the Add-in without our written consent</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">4.3 Intellectual Property</h3>
              <p className="text-muted-foreground leading-relaxed">
                All intellectual property rights in the Add-in, including but not limited to software code, design, 
                trademarks, and documentation, remain the exclusive property of Celsios GmbH. These Terms do not 
                grant you any ownership rights.
              </p>
            </section>

            <section id="prohibited-content">
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">5. Prohibited Content & Acceptable Use Policy</h2>
              
              <h3 className="text-xl font-semibold mb-3">5.1 General Prohibition</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You agree NOT to use Email2Contact to process, extract, or analyze any content that:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Violates applicable laws (including GDPR, criminal law, intellectual property law)</li>
                <li>Contains illegal, fraudulent, or malicious information</li>
                <li>Infringes third-party rights (privacy, intellectual property, personality rights)</li>
                <li>Is used for spam, phishing, or other unauthorized marketing purposes</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">5.2 Specific Prohibited Uses</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Email2Contact may NOT be used to process or extract:
              </p>
              
              <Card className="p-4 mb-4">
                <h4 className="font-semibold mb-3 text-sm">🚫 Illegal Content:</h4>
                <ul className="list-disc pl-6 text-muted-foreground text-sm space-y-1.5">
                  <li>Personal data obtained without legal basis (Art. 6 GDPR)</li>
                  <li>Data of children under 16 years (Art. 8 GDPR) without parental consent</li>
                  <li>Special categories of personal data (Art. 9 GDPR): racial/ethnic origin, political opinions, religious beliefs, health data, biometric data, sexual orientation</li>
                  <li>Stolen, leaked, or illegally obtained email data</li>
                  <li>Data from phishing emails, scam messages, or fraudulent communications</li>
                </ul>
              </Card>

              <Card className="p-4 mb-4">
                <h4 className="font-semibold mb-3 text-sm">⚠️ Malicious Use:</h4>
                <ul className="list-disc pl-6 text-muted-foreground text-sm space-y-1.5">
                  <li>Contact data for spam, cold-calling, or unsolicited marketing (violates ePrivacy Directive)</li>
                  <li>Data scraping or bulk extraction from non-consenting parties</li>
                  <li>Automated processing of email signatures without recipient knowledge (violates GDPR transparency)</li>
                  <li>Circumvention of email provider security mechanisms</li>
                </ul>
              </Card>

              <Card className="p-4 mb-4">
                <h4 className="font-semibold mb-3 text-sm">🛑 Harmful Content:</h4>
                <ul className="list-disc pl-6 text-muted-foreground text-sm space-y-1.5">
                  <li>Email signatures containing hate speech, threats, or discriminatory content</li>
                  <li>Contact data used for identity theft, fraud, or impersonation</li>
                  <li>Data related to criminal activities, money laundering, or terrorism</li>
                </ul>
              </Card>

              <h3 className="text-xl font-semibold mb-3 mt-6">5.3 AI Processing Restrictions</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                When using the AI-powered extraction features (Azure OpenAI, ConvertAPI, Lovable AI), you agree:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>NOT to process email content containing confidential, classified, or trade-secret information without authorization</li>
                <li>NOT to use the Service to extract contact data for purposes that violate the sender's legitimate interests</li>
                <li>NOT to bypass consent mechanisms (e.g., extracting contacts from emails explicitly marked "do not share")</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">5.4 User Responsibility</h3>
              <Card className="app-tile p-4 mb-4 border-primary/30 bg-primary/5">
                <p className="text-sm font-semibold text-primary mb-2">📋 YOU are solely responsible for:</p>
                <ul className="list-disc pl-6 text-primary/90 text-sm space-y-1.5">
                  <li>Ensuring you have a <strong>legal basis</strong> (Art. 6 GDPR) to process contact data (e.g., legitimate interest, consent, contract performance)</li>
                  <li>Complying with <strong>purpose limitation</strong> (Art. 5 GDPR): Contact data may only be used for the purpose it was collected</li>
                  <li>Respecting <strong>data subjects' rights</strong> (Art. 15-22 GDPR): right to access, rectification, erasure, data portability</li>
                  <li>Obtaining necessary <strong>consent</strong> when required (e.g., for marketing purposes under ePrivacy Directive)</li>
                </ul>
              </Card>

              <p className="text-muted-foreground leading-relaxed mb-3 font-semibold">
                Example Compliance Scenarios:
              </p>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <Card className="app-tile p-4 border-emerald-500/30 bg-emerald-500/5">
                  <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400 mb-2">✅ Allowed:</p>
                  <ul className="text-emerald-700/90 dark:text-emerald-400/90 text-sm space-y-1.5">
                    <li>Extracting contact data from a business email you received directly (legitimate interest for business communication)</li>
                    <li>Saving contacts from email signatures where the sender provided their information voluntarily</li>
                  </ul>
                </Card>
                <Card className="app-tile p-4 border-destructive/30 bg-destructive/5">
                  <p className="text-sm font-semibold text-destructive mb-2">❌ Prohibited:</p>
                  <ul className="text-destructive/90 text-sm space-y-1.5">
                    <li>Extracting contacts from forwarded emails where you are not the original recipient</li>
                    <li>Using extracted contact data for cold-calling or spam without explicit consent</li>
                  </ul>
                </Card>
              </div>

              <h3 className="text-xl font-semibold mb-3 mt-6">5.5 Content Monitoring & Enforcement</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li><strong>No Pre-Screening</strong>: We do NOT pre-screen email content or extracted contact data</li>
                <li><strong>No Liability for User Content</strong>: We are NOT responsible for content you choose to process via Email2Contact (see § 7 Para. 1 TMG "Provider Liability")</li>
                <li><strong>Reporting Violations</strong>: If you believe another user is violating this policy, report it to support@email2contact.com</li>
                <li><strong>Termination for Abuse</strong>: We reserve the right to immediately terminate accounts that violate this policy without prior notice or refund</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">5.6 Indemnification for Policy Violations</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If your use of Email2Contact violates this Acceptable Use Policy and results in claims, fines, or legal actions against Celsios GmbH:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>You agree to <strong>indemnify and hold us harmless</strong> from all damages, losses, and legal fees</li>
                <li>This includes GDPR fines imposed due to your misuse of the Service (Art. 82 GDPR)</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">5.7 Legal Basis Clarification</h3>
              <Card className="app-tile p-4 mb-4 border-amber-500/30 bg-amber-500/5">
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  Email2Contact provides a <strong>technical tool</strong> for contact extraction. We do NOT determine the purposes or means of processing contact data—YOU do (Art. 4(7) GDPR "Controller").
                </p>
              </Card>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li><strong>You</strong> are the <strong>Data Controller</strong> for contact data you extract and store</li>
                <li><strong>We</strong> are a <strong>Data Processor</strong> only for ephemeral AI processing (ConvertAPI, Azure OpenAI, Lovable AI)</li>
                <li>You must ensure compliance with GDPR <strong>before</strong> using the Service to process personal data</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">6. User Obligations</h2>
              
              <h3 className="text-xl font-semibold mb-3">6.1 Account Requirements</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                To use Email2Contact, you must:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Have a valid Microsoft 365 or Outlook account</li>
                <li>Be at least 18 years of age (or the age of legal adulthood in your jurisdiction)</li>
                <li>Provide accurate and complete information when requested</li>
                <li>Maintain the security of your Microsoft account credentials</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">6.2 Compliance with Laws</h3>
              <p className="text-muted-foreground leading-relaxed">
                You are solely responsible for ensuring that your use of the Add-in complies with all applicable laws, 
                including but not limited to data protection laws (GDPR), intellectual property laws, and anti-spam 
                regulations.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">6.3 Data Accuracy</h3>
              <p className="text-muted-foreground leading-relaxed">
                You acknowledge that Email2Contact uses automated extraction algorithms and that the accuracy of 
                extracted contact information depends on the quality and format of email signatures. You are responsible 
                for reviewing and verifying all contact data before saving it.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">7. Fees and Payment</h2>
              
              <h3 className="text-xl font-semibold mb-3">7.1 Free Trial</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Email2Contact may offer a free trial period. The duration and terms of the trial will be communicated 
                at the time of installation.
              </p>

              <h3 className="text-xl font-semibold mb-3">7.2 Subscription Plans</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                After the trial period, continued use requires a paid subscription. Pricing and plan details are 
                available at https://email2contact.com.
              </p>

              <h3 className="text-xl font-semibold mb-3">7.3 Payment Terms</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Subscriptions are billed in advance on a monthly or annual basis</li>
                <li>Payments are processed through secure third-party payment providers</li>
                <li>All fees are exclusive of applicable taxes (VAT will be added for EU customers)</li>
                <li>Subscriptions auto-renew unless canceled before the renewal date</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">7.4 Refund Policy</h3>
              <p className="text-muted-foreground leading-relaxed">
                Under German consumer protection law (§ 312g BGB), you have a 14-day right of withdrawal for online 
                purchases. Refunds for subscription fees are handled in accordance with our refund policy, which can 
                be requested at support@email2contact.com.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">8. Service Availability and Modifications</h2>
              
              <h3 className="text-xl font-semibold mb-3">8.1 Service Availability</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We strive to maintain high availability of the Service but do not guarantee uninterrupted access. 
                The Service may be temporarily unavailable due to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Scheduled maintenance</li>
                <li>Emergency repairs</li>
                <li>Third-party service outages (e.g., Microsoft infrastructure)</li>
                <li>Force majeure events</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">8.2 Modifications to Service</h3>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time, with 
                or without notice. We will make reasonable efforts to notify users of significant changes.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">8.3 Updates</h3>
              <p className="text-muted-foreground leading-relaxed">
                The Add-in may receive automatic updates to improve functionality, security, or compatibility. By using 
                Email2Contact, you consent to receiving such updates.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">9. Disclaimers and Limitation of Liability</h2>
              
              <h3 className="text-xl font-semibold mb-3">9.1 "As Is" Service</h3>
              <Card className="app-tile p-4 mb-4 border-amber-500/30 bg-amber-500/5">
                <p className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-2">⚠️ Important Legal Notice:</p>
                <p className="text-sm text-amber-800">
                  EMAIL2CONTACT IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS 
                  OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR 
                  PURPOSE, OR NON-INFRINGEMENT.
                </p>
              </Card>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We do not warrant that:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>The Service will meet your specific requirements</li>
                <li>Contact extraction will be 100% accurate in all cases</li>
                <li>The Service will be error-free, secure, or uninterrupted</li>
                <li>Defects will be corrected within a specific timeframe</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">9.2 Limitation of Liability</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                To the maximum extent permitted by German law:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li><strong>No Liability for Indirect Damages</strong>: We are not liable for indirect, incidental, 
                special, consequential, or punitive damages, including but not limited to loss of profits, data, or 
                business opportunities.</li>
                <li><strong>Maximum Liability</strong>: Our total liability for any claim arising from these Terms or 
                the Service shall not exceed the amount you paid for the Service in the 12 months preceding the claim.</li>
                <li><strong>Statutory Rights</strong>: Nothing in these Terms excludes or limits our liability for death 
                or personal injury caused by negligence, fraud, or any liability that cannot be excluded under German law.</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">9.3 Microsoft Services</h3>
              <p className="text-muted-foreground leading-relaxed">
                Email2Contact integrates with Microsoft services. We are not responsible for any failures, errors, or 
                disruptions caused by Microsoft's infrastructure, APIs, or account policies.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">10. Data Protection and Privacy</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Your use of Email2Contact is also governed by our Privacy Policy, which explains how we process personal 
                data in compliance with the GDPR.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                By using the Service, you acknowledge and agree to the data processing practices described in our{" "}
                <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">11. Termination</h2>
              
              <h3 className="text-xl font-semibold mb-3">11.1 Termination by You</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You may terminate your use of Email2Contact at any time by:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Uninstalling the Add-in from Outlook</li>
                <li>Canceling your subscription (if applicable)</li>
                <li>Contacting us at support@email2contact.com</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">11.2 Termination by Us</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We reserve the right to suspend or terminate your access to the Service immediately, without prior 
                notice, if:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>You breach these Terms</li>
                <li>You fail to pay applicable fees</li>
                <li>Your use of the Service poses a security or legal risk</li>
                <li>We are required to do so by law or court order</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">11.3 Effect of Termination</h3>
              <p className="text-muted-foreground leading-relaxed">
                Upon termination, your license to use the Add-in immediately ceases. No refunds will be provided for 
                unused subscription periods, except as required by law.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">12. Indemnification</h2>
              <p className="text-muted-foreground leading-relaxed">
                You agree to indemnify, defend, and hold harmless Celsios GmbH, its directors, employees, and 
                affiliates from any claims, damages, losses, liabilities, and expenses (including legal fees) arising 
                from:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                <li>Your use of the Add-in in violation of these Terms</li>
                <li>Your violation of any applicable laws or third-party rights</li>
                <li>Your unauthorized use of contact data obtained through the Service</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">13. Governing Law and Dispute Resolution</h2>
              
              <h3 className="text-xl font-semibold mb-3">13.1 Governing Law</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                These Terms are governed by and construed in accordance with the laws of the Federal Republic of 
                Germany, excluding the UN Convention on Contracts for the International Sale of Goods (CISG).
              </p>

              <h3 className="text-xl font-semibold mb-3">13.2 Jurisdiction</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                For consumers (persons acting outside their trade, business, or profession), statutory jurisdiction 
                applies. For all other disputes, the exclusive place of jurisdiction is Munich, Germany.
              </p>

              <h3 className="text-xl font-semibold mb-3">13.3 Dispute Resolution</h3>
              <p className="text-muted-foreground leading-relaxed">
                Before initiating legal proceedings, we encourage you to contact us at support@email2contact.com to resolve 
                disputes amicably. We are committed to finding fair solutions.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">14. Changes to Terms</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We reserve the right to modify these Terms at any time. Changes will be effective:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li><strong>For existing users</strong>: 30 days after notification via email or in-app notice</li>
                <li><strong>For new users</strong>: Immediately upon posting on https://email2contact.com/terms</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                Continued use of the Service after changes take effect constitutes acceptance of the updated Terms. 
                If you do not agree to the changes, you must stop using the Add-in.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">15. Miscellaneous</h2>
              
              <h3 className="text-xl font-semibold mb-3">15.1 Entire Agreement</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and 
                Celsios GmbH regarding the Service.
              </p>

              <h3 className="text-xl font-semibold mb-3">15.2 Severability</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions 
                will continue in full force and effect.
              </p>

              <h3 className="text-xl font-semibold mb-3">15.3 Waiver</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Our failure to enforce any provision of these Terms does not constitute a waiver of that provision.
              </p>

              <h3 className="text-xl font-semibold mb-3">15.4 Assignment</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You may not assign or transfer these Terms or any rights granted hereunder without our prior written 
                consent. We may assign these Terms to any successor or affiliate.
              </p>

              <h3 className="text-xl font-semibold mb-3">15.5 Language</h3>
              <p className="text-muted-foreground leading-relaxed">
                These Terms are drafted in English. In case of conflicts between translated versions, the English 
                version shall prevail.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">16. Contact Information</h2>
              <Card className="app-tile p-6 bg-muted/30">
                <p className="text-sm text-muted-foreground mb-4">
                  If you have any questions about these Terms, please contact us:
                </p>
                <p className="font-semibold mb-2">Celsios GmbH</p>
                <p className="text-sm text-muted-foreground">
                  Email:{" "}
                  <a href="mailto:support@email2contact.com" className="text-primary hover:underline">
                    support@email2contact.com
                  </a>
                  <br />
                  Website:{" "}
                  <a href="https://email2contact.com" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                    https://email2contact.com
                  </a>
                  <br />
                  Support:{" "}
                  <a href="/support" className="text-primary hover:underline">
                    Support Page
                  </a>
                </p>
              </Card>
            </section>

            <Card className="p-6 bg-primary/5 border-primary/20 mt-12">
              <p className="text-sm text-muted-foreground text-center">
                By clicking "Install" or using Email2Contact, you acknowledge that you have read, understood, 
                and agree to be bound by these Terms of Service.
              </p>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Terms;
